import type { RefObject } from "react";

import { useEffect, useCallback, useRef } from "react";

import type { ChatMessage as ChatMessageType } from "~/chat/types/chat";

type UseChatAnchorScrollProps<T extends HTMLElement> = {
  elementRef?: RefObject<T>;
  messages?: ChatMessageType[];
  autoScrollDisabled?: boolean;
  conversationId?: number;
};

/**
 * @description
 * A hook that manages the scrolling behavior of a chat component.
 * Scrolls to the bottom of the chat when existing conversation is loaded.
 * Scrolls smoothly to the last user message when new message is added.
 *
 */
function useChatAnchorScroll<T extends HTMLElement>({
  elementRef,
  messages = [],
  autoScrollDisabled = false,
  conversationId,
}: UseChatAnchorScrollProps<T>) {
  const previousConversationIdRef = useRef<number | undefined>(conversationId);
  const previousMessagesLengthRef = useRef<number>(messages.length);

  const scrollToBottom = useCallback(
    (isSmooth = false) => {
      const anchorElement = elementRef?.current;

      if (!anchorElement || autoScrollDisabled) {
        return;
      }

      anchorElement.scroll({
        left: 0,
        behavior: isSmooth ? "smooth" : "auto",
        top: anchorElement.scrollHeight,
      });
    },
    [elementRef, autoScrollDisabled],
  );

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    const previousMessagesLength = previousMessagesLengthRef.current;

    if (previousConversationId !== conversationId && messages.length > 0) {
      scrollToBottom(); // Initial conversation load
    } else if (
      previousConversationId === conversationId &&
      previousMessagesLength < messages.length
    ) {
      scrollToBottom(true); // New message added to the conversation
    }

    previousConversationIdRef.current = conversationId;
    previousMessagesLengthRef.current = messages.length;
  }, [scrollToBottom, messages.length, conversationId]);

  return { scrollToBottom };
}

export default useChatAnchorScroll;
